
import axios from "axios"
import { useState, useEffect, useMemo, useCallback } from "react"
import { useTranslation } from "react-i18next"
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  Grid,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  Paper,
  Breadcrumbs,
  Link,
  IconButton,
  useTheme,
  useMediaQuery,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material"
import {
  PlayArrow as PlayArrowIcon,
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  CropFree as CropFreeIcon,
} from "@mui/icons-material"
import EntityList from "../../components/EntityList"
import api from "../../services/api"
import HorseLoader from "../../components/loadingHorse"
import type { Camera, Tenant } from "../../interfaces/index"
import { streamService } from "../../services/streamService"
import StreamDialog from "../../components/StreamDialog"
import RoiEditorDialog, { type RoiRegions } from "../../components/RoiEditorDialog"
import type { RoiPoint } from "../../components/RoiEditor"
import { useIsMobile, useIsTablet } from "../../hooks/useMediaQuery"
import { useCryptoNavigation, useAppNavigation } from "../../App"

// Interface para as propriedades do componente
interface CameraListProps {
  tenant_id?: number
  
}

// Interface para o mapa de cavalos
interface HorsesMap {
  [key: number]: string
}

// Interface para os filtros ativos
interface ActiveFilters {
  searchText: string
  cameraStatus: 'all' | 'ativo' | 'inativo'
}
// Função helper para obter o tenant_id correto
const getTenantId = (): number => {
  // 1. Tentar pegar do usuário autenticado (tenant fixo associado ao usuário)
  try {
    const userStr = localStorage.getItem('user')
    if (userStr) {
      const user = JSON.parse(userStr)
      const userTenant = user?.userTenant
      if (userTenant) {
        console.log('🎯 CameraList: Tenant do usuário:', userTenant)
        return Number(userTenant)
      }
    }
  } catch (e) {
    console.error('Erro ao buscar tenant do usuário:', e)
  }
  
  // 2. Tentar pegar do tenant selecionado no início da sessão
  const selectedTenantId = localStorage.getItem('selectedTenantId')
  if (selectedTenantId) {
    console.log('🎯 CameraList: Tenant selecionado:', selectedTenantId)
    return Number(selectedTenantId)
  }
  
  // 3. Fallback para 1 apenas se não encontrar nada
  console.warn('⚠️ CameraList: Nenhum tenant encontrado, usando fallback tenant_id=1')
  return 1
}

const CameraList: React.FC<CameraListProps> = ({ tenant_id }) => {
  const { t } = useTranslation()
  const { navigateToPage } = useCryptoNavigation()
  const { getParam } = useAppNavigation()
  
  // ✅ CORRIGIDO: Obtém tanto tenantId quanto recorderId dos parâmetros
  const tenantIdFromParams = getParam('tenantId')
  const recorderIdFromParams = getParam('recorderId')
  
  // ✅ CORRIGIDO: Usar a mesma lógica do StreamDialog
  // Prioridade: prop tenant_id > parâmetro tenantId > getTenantId()
  const finalTenantId = tenant_id || (tenantIdFromParams ? Number(tenantIdFromParams) : null) || getTenantId()
  const recorderId = recorderIdFromParams ? Number(recorderIdFromParams) : null

  // Log do tenant_id sendo usado
  useEffect(() => {
    console.log('🎯 CameraList montado com finalTenantId:', finalTenantId, {
      tenant_id_prop: tenant_id,
      tenantIdFromParams,
      recorderId
    })
  }, [finalTenantId, tenant_id, tenantIdFromParams, recorderId])

  const theme = useTheme()
  const isMobile = useIsMobile()
  const isTablet = useIsTablet()
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"))

  const [selectedCamera, setSelectedCamera] = useState<Camera | null>(null)
  const [openDetailsDialog, setOpenDetailsDialog] = useState<boolean>(false)
  const [openStreamDialog, setOpenStreamDialog] = useState<boolean>(false)
  const [streamUrl, setStreamUrl] = useState<string | null>(null)
  const [loadingStream, setLoadingStream] = useState<boolean>(false)
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0)
  const [roiCamera, setRoiCamera] = useState<Camera | null>(null)
  const [roiDialogOpen, setRoiDialogOpen] = useState<boolean>(false)
  const [roiSaving, setRoiSaving] = useState<boolean>(false)
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false)
  const [snackbarMessage, setSnackbarMessage] = useState<string>("")
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "info" | "warning" | "error">("info")
  const [loadingTenant, setLoadingTenant] = useState<boolean>(true)
  const [tenant, setTenant] = useState<Tenant | null>(null)
  const [streamErrorMessage, setStreamErrorMessage] = useState<string | null>(null)

  // Estados originais
  const [horsesMap, setHorsesMap] = useState<HorsesMap>({})
  const [loading, setLoading] = useState<boolean>(true)

  // Novos estados para filtros
  const [activeFilters, setActiveFilters] = useState<ActiveFilters>({
    searchText: '',
    cameraStatus: 'all'
  })

  // Estado para controlar se os filtros foram aplicados
  const [hasActiveFilters, setHasActiveFilters] = useState<boolean>(false)

  // ✅ NOVO: Estado para armazenar o nome do gravador (para breadcrumb)
  const [recorderName, setRecorderName] = useState<string>('')

  // ✅ OTIMIZADO: Memoizar título baseado nos estados
  const pageTitle = useMemo(() => {
    if (recorderId && recorderName) {
      return t("cameras.titleWithRecorder", { recorder: recorderName })
    }
    return t("nav.cameras")
  }, [recorderId, recorderName, t])

  // ✅ OTIMIZADO: Buscar nome do gravador apenas quando necessário
  useEffect(() => {
    if (!recorderId) return

    let mounted = true
    const fetchRecorderName = async () => {
      try {
        const response = await api.get(`/recorder/${finalTenantId}/${recorderId}`)
        if (!mounted) return
        
        const recorderData = response.data?.recorder || response.data
        const name = recorderData?.recorderName || t("cameras.recorderFallback", { id: recorderId })
        setRecorderName(name)
      } catch (error) {
        if (mounted) {
          setRecorderName(t("cameras.recorderFallback", { id: recorderId }))
        }
      }
    }

    fetchRecorderName()

    return () => {
      mounted = false
    }
  }, [recorderId, finalTenantId, t])

  // ✅ OTIMIZADO: Carregar tenant apenas uma vez com cleanup
  useEffect(() => {
    let mounted = true
    
    const fetchTenant = async () => {
      try {
        setLoadingTenant(true)
        const response = await api.get(`/tenant/${finalTenantId}`)
        
        if (!mounted) return

        // Processar a resposta para extrair o tenant
        let tenantData = null

        if (response.data && response.data.tenant) {
          tenantData = response.data.tenant
        } else if (response.data && typeof response.data === "object") {
          if (response.data.tenantId && response.data.tenantName) {
            tenantData = response.data
          } else {
            const tenantEntries = Object.entries(response.data).find(
              ([key, value]) =>
                typeof value === "object" && value !== null && "tenantId" in value && "tenantName" in value,
            )
            if (tenantEntries) {
              tenantData = tenantEntries[1]
            }
          }
        }

        setTenant(tenantData)
      } catch (error) {
        // Silenciar erro para evitar spam no console
      } finally {
        if (mounted) {
          setLoadingTenant(false)
        }
      }
    }

    fetchTenant()

    return () => {
      mounted = false
    }
  }, [finalTenantId])

  // ✅ OTIMIZADO: Memoizar efeito de atualização do estado de filtros ativos
  useEffect(() => {
    const hasFilters = activeFilters.searchText.trim() !== '' || activeFilters.cameraStatus !== 'all'
    if (hasActiveFilters !== hasFilters) {
      setHasActiveFilters(hasFilters)
    }
  }, [activeFilters, hasActiveFilters])

  const handleCloseStreamDialog = useCallback(() => {
  setOpenStreamDialog(false)
  setStreamUrl(null)
  setSelectedCamera(null)
  setLoadingStream(false)
}, [])
  // ✅ OTIMIZADO: Memoizar funções de navegação
  const handleBackToTenants = useCallback(() => {
    navigateToPage('proprietarios', { mode: 'list' })
  }, [navigateToPage])

  const handleBackToRecorders = useCallback(() => {
    navigateToPage('gravadores', { mode: 'list', tenantId: String(finalTenantId) })
  }, [navigateToPage, finalTenantId])

  const handleBackHome = useCallback(() => {
    navigateToPage('dashboard')
  }, [navigateToPage])

  // ✅ OTIMIZADO: Função para navegar para novo
  const handleNew = useCallback(() => {
    const params: any = { mode: 'new', tenantId: String(finalTenantId) }
    if (recorderId) {
      params.recorderId = String(recorderId)
    }
    navigateToPage('cameras', params)
  }, [navigateToPage, finalTenantId, recorderId])

  // ✅ OTIMIZADO: Função para navegar para edição
  const handleEdit = useCallback((cameraId: number | string) => {
    const params: any = { mode: 'edit', id: String(cameraId), tenantId: String(finalTenantId) }
    if (recorderId) {
      params.recorderId = String(recorderId)
    }
    navigateToPage('cameras', params)
  }, [navigateToPage, finalTenantId, recorderId])

  // ✅ OTIMIZADO: useEffect para processar dados da API sem spam de debug
  useEffect(() => {
    let mounted = true
    
    const processApiData = async (): Promise<void> => {
      try {
        setLoading(true)

        // Buscar dados de stalls/horses
        const promises = [
          api.get(`/stall/${finalTenantId}`).catch(() => ({ data: [] })),
          api.get(`/horse/${finalTenantId}`).catch(() => ({ data: [] }))
        ]
        
        const [stallsResponse, horsesResponse] = await Promise.all(promises)
        
        if (!mounted) return

        const stallsData = stallsResponse.data.stalls || stallsResponse.data || []
        const horsesData = horsesResponse.data.horses || horsesResponse.data || []

        // Criar mapa de baia -> cavalo
        const newHorsesMap: HorsesMap = {}

        // Processar stalls
        if (Array.isArray(stallsData)) {
          stallsData.forEach((stall: any) => {
            if (stall.stallId && stall.horseName) {
              newHorsesMap[stall.stallId] = stall.horseName
            }
          })
        }

        // Processar horses
        if (Array.isArray(horsesData)) {
          horsesData.forEach((horse: any) => {
            if (horse.stallId && horse.horseName) {
              newHorsesMap[horse.stallId] = horse.horseName
            }
          })
        }

        if (mounted) {
          setHorsesMap(newHorsesMap)
          setRefreshTrigger((prev) => prev + 1)
          setLoading(false)
        }
      } catch (error) {
        if (mounted) {
          setLoading(false)
        }
      }
    }
    
    processApiData()
    
    return () => {
      mounted = false
    }
  }, [finalTenantId])

  // ✅ OTIMIZADO: Memoizar transformData para evitar re-renderizações
  const transformData = useCallback((data: any): any[] => {
    // Obter o array de câmeras
    let cameras: any[] = []

    if (Array.isArray(data)) {
      cameras = data
    } else if (data && data.cameras && Array.isArray(data.cameras)) {
      cameras = data.cameras
    } else if (data && typeof data === "object") {
      try {
        const values = Object.values(data)
        const arrayValues = values.filter((item) => Array.isArray(item))
        cameras = arrayValues.length > 0 ? arrayValues[0] : []
      } catch (error) {
        cameras = []
      }
    }

    // ✅ FILTRO POR RECORDID - Otimizado sem debug
    if (recorderId) {
      cameras = cameras.filter((camera: any) => {
        // Usar cameraRecorder como principal, mas verificar outros campos se não existir
        let cameraRecorderId = camera.cameraRecorder
        if (cameraRecorderId === undefined || cameraRecorderId === null) {
          cameraRecorderId = camera.recorderId || camera.recorder_id || camera.recorderID || camera.camera_recorder
        }
        
        return Number(cameraRecorderId) === Number(recorderId)
      })
    }

    // Processar cada câmera adicionando informações do cavalo
    let result = cameras.map((camera: any) => ({
      ...camera,
      horseFromStall: camera.cameraStall && horsesMap[camera.cameraStall] ? horsesMap[camera.cameraStall] : null,
    }))

    // Aplicar filtros de busca apenas se houver filtros ativos
    const hasSearchFilter = activeFilters.searchText.trim() !== ''
    const hasStatusFilter = activeFilters.cameraStatus !== 'all'

    if (hasSearchFilter || hasStatusFilter) {
      result = result.filter((camera: any) => {
        if (hasSearchFilter) {
          const searchText = activeFilters.searchText.toLowerCase()
          const stallName = (camera.stallName || '').toLowerCase()
          const horseName = (camera.horseFromStall || '').toLowerCase()
          
          if (!(stallName.includes(searchText) || horseName.includes(searchText))) {
            return false
          }
        }

        if (hasStatusFilter) {
          const isativo = camera.cameraStatus === 1
          if (activeFilters.cameraStatus === 'ativo' && !isativo) return false
          if (activeFilters.cameraStatus === 'inativo' && isativo) return false
        }

        return true
      })
    }

    return result
  }, [recorderId, horsesMap, activeFilters])

  // ✅ OTIMIZADO: Memoizar funções de filtro
  const handleClearFilters = useCallback(() => {
    setActiveFilters({
      searchText: '',
      cameraStatus: 'all'
    })
  }, [])

  const handleApplyFilters = useCallback(() => {
    setRefreshTrigger((prev) => prev + 1)
    const hasFilters = activeFilters.searchText.trim() !== '' || activeFilters.cameraStatus !== 'all'
    setHasActiveFilters(hasFilters)
    showMessage(`${t('cameras.filtersApplied')} ${hasFilters ? t('cameras.resultsFiltered') : t('cameras.resultsAll')}`, 'info')
  }, [activeFilters])

  // ✅ OTIMIZADO: Memoizar função de mensagens
  const showMessage = useCallback((message: string, severity: "success" | "info" | "warning" | "error" = "info") => {
    setSnackbarMessage(message)
    setSnackbarSeverity(severity)
    setSnackbarOpen(true)
  }, [])

  // ✅ OTIMIZADO: Memoizar função de detalhes
  const handleShowDetails = useCallback((camera: Camera): void => {
    setSelectedCamera(camera)
    setOpenDetailsDialog(true)
  }, [])

  // Exceção de permissão: o manager NÃO edita nada no admin, mas se não tiver um
  // tenant associado a ele pode editar SOMENTE o ROI das câmeras.
  const canEditRoi = useMemo(() => {
    try {
      const u = JSON.parse(localStorage.getItem("user") || "null")
      const role = String(u?.Role || "").toLowerCase().trim()
      return role === "manager" && !u?.userTenant
    } catch {
      return false
    }
  }, [])

  const parseRoiPoints = (roi: unknown): RoiPoint[] => {
    if (!roi) return []
    try {
      const p = typeof roi === "string" ? JSON.parse(roi) : roi
      return Array.isArray(p) ? (p as RoiPoint[]) : []
    } catch {
      return []
    }
  }

  /** Serializa uma região, ou null quando não definida (menos de 3 pontos). */
  const serializarRoi = (pts: RoiPoint[] | undefined): string | null =>
    pts && pts.length >= 3 ? JSON.stringify(pts) : null

  // Salva as TRÊS regiões: relê o registro completo e reescreve só esses campos
  // (evita PUT parcial zerar outras colunas). Mesmo shape/serialização do CameraForm.
  const handleSaveRoi = useCallback(async (regioes: RoiRegions) => {
    if (!roiCamera) return
    const camId = roiCamera.cameraId
    try {
      setRoiSaving(true)
      const resp = await api.get(`/camera/${finalTenantId}/${camId}`)
      const full = resp.data?.camera ?? resp.data ?? {}
      const payload = {
        cameraName: full.cameraName ?? "",
        cameraModel: full.cameraModel ?? "",
        cameraChannel: Number(full.cameraChannel),
        cameraStatus: Number(full.cameraStatus),
        cameraRecorder: Number(full.cameraRecorder),
        cameraStall: Number(full.cameraStall),
        stableId: Number(full.stableId) || 0,
        tenantId: Number(full.tenantId ?? finalTenantId),
        cameraRoi: serializarRoi(regioes.roi),
        cameraRoiWater: serializarRoi(regioes.water),
        cameraRoiFeed: serializarRoi(regioes.feed),
      }
      await api.put(`/camera/${finalTenantId}/${camId}`, payload)
      showMessage(t("cameras.roiSaved", "ROI atualizado."), "success")
      setRoiDialogOpen(false)
      setRoiCamera(null)
      setOpenDetailsDialog(false)
      setRefreshTrigger((v) => v + 1)
    } catch (e) {
      console.error("Erro ao salvar ROI:", e)
      showMessage(t("cameras.roiSaveError", "Não foi possível salvar o ROI."), "error")
    } finally {
      setRoiSaving(false)
    }
  }, [roiCamera, finalTenantId, t])

  // ✅ OTIMIZADO: Memoizar função de stream
  const handleShowStream = useCallback(async (cameraId: number, camera: Camera): Promise<void> => {
  if (camera.cameraStatus !== 1) {
    showMessage(t("cameras.cameraInactive"), "warning")
    return
  }

  try {
    const token = localStorage.getItem("token")

    if (!token) {
      showMessage(t("cameras.notAuthenticated"), "error")
      window.location.href = "/login"
      return
    }

    console.log("🎥 Iniciando stream com tenant_id:", finalTenantId)
    setSelectedCamera(camera)
    setStreamErrorMessage(null)
    setStreamUrl(null)
    setLoadingStream(true)
    setOpenStreamDialog(true)

    console.log(`>> BUSCANDO DADOS COMPLETOS: /camera/${finalTenantId}/${cameraId}`)
    const cameraResponse = await api.get(`/camera/${finalTenantId}/${cameraId}`)

    if (!cameraResponse.data || !cameraResponse.data.camera) {
      throw new Error(t("cameras.cameraDataNotFound"))
    }

    const fullCameraData = cameraResponse.data.camera

    const enrichedCamera = {
      ...camera,
      recorderExtIP: fullCameraData.recorderExtIP,
      recorderExtHTTP: fullCameraData.recorderExtHTTP,
      recorderExtRTSP: fullCameraData.recorderExtRTSP,
      recorderName: fullCameraData.recorderName,
      recorderTypeName: fullCameraData.recorderTypeName,
    }

    const result = await streamService.prepareStreamData(enrichedCamera, null, finalTenantId)

    if (!result.success || !result.url) {

      throw new Error(
        result.errorMessage ||
        t("cameras.streamStartFailed")
      )
    }

    setStreamUrl(result.url)
    setLoadingStream(false)
    showMessage(t("cameras.streamStarted"), "success")
  } catch (error: any) {
    console.error("Erro ao carregar stream:", error)

    let message =
      error?.message ||
      t("cameras.streamLoadError")

    if (axios.isAxiosError(error) && error.response) {
      const errorMessage = error.response.data?.message || ""

      console.log("🔍 ANÁLISE DO ERRO:")
      console.log("  - Status:", error.response.status)
      console.log("  - Mensagem:", errorMessage)

      if (errorMessage.includes("camera is not defined")) {
        message = t("cameras.serverOutdated")
      } else if (errorMessage.includes("FFmpeg")) {
        message = t("cameras.ffmpegFailed")
      }
    }

    setStreamErrorMessage(message)
showMessage(message, "error")
setStreamUrl(null)
setLoadingStream(false)
  } finally {
      setLoadingStream(false)
  }
}, [finalTenantId, showMessage, t])

  // ✅ OTIMIZADO: Memoizar colunas para evitar re-renderizações
  const columns = useMemo(() => [
    {
      id: "cameraName",
      label: t("fields.name"),
      minWidth: 150,
    },
    {
      id: "cameraStatus",
      label: t("fields.status"),
      minWidth: 100,
      align: "center" as const,
      mobileHideLabel: true,
      format: (value: number) => (
        <Chip
          label={value === 1 ? t("common.active") : t("common.inactive")}
          color={value === 1 ? "success" : "error"}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      id: "stableName",
      label: t("fields.stable"),
      minWidth: 120,
      hide: isMobile,
    },
    {
      id: "cameraStall",
      label: t("stalls.nameLabel"),
      minWidth: 120,
      format: (value: number, row: Camera) => row.stallName || "-",
      hide: isMobile,
    },
    {
      id: "cameraRecorder",
      label: t("fields.recorder"),
      minWidth: 120,
      format: (value: number, row: Camera) => row.recorderName || "-",
      hide: recorderId ? false : (isMobile || isTablet),
    },
    {
      id: "horseFromStall",
      label: t("fields.horse"),
      minWidth: 120,
      format: (value: string | null) => value || "-",
      // visível no mobile (info útil além do nome da câmera)
    },
    {
      id: "cameraRoi",
      label: "ROI",
      minWidth: 90,
      align: "center" as const,
      hide: isMobile,
      // Ordena pelo NÚMERO de pontos, não pela string JSON. Sem ROI/ inválido → -1.
      sortValue: (row: Camera) => {
        const v = row.cameraRoi
        if (!v) return -1
        try {
          const pts = typeof v === "string" ? JSON.parse(v) : v
          return Array.isArray(pts) ? pts.length : -1
        } catch {
          return -1
        }
      },
      format: (value: string | null) => {
        if (!value) return <Chip label={t("cameras.roiNotDefined")} size="small" variant="outlined" color="default" />
        try {
          const pts = typeof value === "string" ? JSON.parse(value) : value
          return Array.isArray(pts) && pts.length >= 3
            ? <Chip label={t("cameras.roiPoints", { count: pts.length })} size="small" variant="outlined" color="success" />
            : <Chip label={t("cameras.roiInvalid")} size="small" variant="outlined" color="warning" />
        } catch {
          return <Chip label={t("cameras.roiInvalid")} size="small" variant="outlined" color="warning" />
        }
      },
    },
  ], [isMobile, isTablet, recorderId, t])

  return (
    <>
      {/* ✅ CORRIGIDO: Breadcrumbs com contexto de gravador */}
      <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box sx={{ flexGrow: 1, minWidth: isMobile ? "100%" : "auto" }}>
            <Breadcrumbs aria-label="breadcrumb" sx={{ flexWrap: "wrap" }}>
              {loadingTenant ? (
                <CircularProgress size={20} />
              ) : (
                <Typography
                  color="text.primary"
                  sx={{
                    maxWidth: { xs: "150px", sm: "300px" },
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {tenant?.tenantName || t("cameras.ownerFallback")}
                </Typography>
              )}
              
              {/* ✅ NOVO: Mostrar gravador no breadcrumb se vier de gravador específico */}
              {recorderId && (
                <Link
                  color="inherit"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    handleBackToRecorders()
                  }}
                  sx={{
                    maxWidth: { xs: "120px", sm: "200px" },
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {recorderName || t("cameras.recorderFallback", { id: recorderId })}
                </Link>
              )}
            </Breadcrumbs>
          </Box>
          <Button
            startIcon={<ArrowBackIcon />}
            variant="outlined"
            onClick={recorderId ? handleBackToRecorders : handleBackHome}
            size={isMobile ? "small" : "medium"}
            sx={{ mt: isMobile ? 1 : 0 }}
          >
            {recorderId ? t('cameras.backToRecorders') : t('common.back')}
          </Button>
        </Box>
      </Paper>

      {/* PAINEL DE FILTROS - Só mostrar se não estiver filtrando por gravador específico */}
      {!recorderId && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FilterListIcon />
              {t("cameras.filters")}
              {hasActiveFilters && (
                <Chip 
                  label={t("cameras.activeFilters", { count: (activeFilters.searchText ? 1 : 0) + (activeFilters.cameraStatus !== 'all' ? 1 : 0) })}
                  color="primary" 
                  size="small" 
                />
              )}
            </Typography>
          </Box>

          <Grid container spacing={2} alignItems="center">
            {/* Busca por texto */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={t("cameras.searchLabel")}
                placeholder={t("cameras.searchPlaceholder")}
                value={activeFilters.searchText}
                onChange={(e) => setActiveFilters(prev => ({ ...prev, searchText: e.target.value }))}
                variant="outlined"
                size="small"
              />
            </Grid>

            {/* Filtro por status */}
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>{t("cameras.statusLabel")}</InputLabel>
                <Select
                  value={activeFilters.cameraStatus}
                  onChange={(e) => setActiveFilters(prev => ({ 
                    ...prev, 
                    cameraStatus: e.target.value as 'all' | 'ativo' | 'inativo' 
                  }))}
                  label={t("cameras.statusLabel")}
                >
                  <MenuItem value="all">{t("cameras.filterAll")}</MenuItem>
                  <MenuItem value="ativo">{t("common.active")}</MenuItem>
                  <MenuItem value="inativo">{t("common.inactive")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Botões de ação */}
            <Grid item xs={12} md={5}>
              <Box sx={{ display: "flex", gap: 1, justifyContent: { xs: "stretch", md: "flex-end" } }}>
                <Button
                  variant="contained"
                  onClick={handleApplyFilters}
                  size="small"
                  sx={{ flex: { xs: 1, flex: 'none' } }}
                >
                  {t("common.apply")}
                </Button>
                <Button
                  startIcon={<ClearIcon />}
                  variant="contained"
                  onClick={handleClearFilters}
                  size="small"
                  sx={{
                    flex: { xs: 1, md: 'none' },
                    backgroundColor: '#d0a029',  // ouro velho
                    color: 'black',              // texto preto
                    '&:hover': {
                      backgroundColor: '#b88e23' // dourado escuro no hover
                    }
                  }}
                  disabled={!hasActiveFilters}
                >
                  {t("common.clear")}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <HorseLoader />
        </Box>
      ) : (
        <EntityList
          title={pageTitle}
          endpoint={`/camera/${finalTenantId}`}
          columns={columns}
          addPath=""
          editPathPrefix=""
          idField="cameraId"
          dataPath="cameras"
          onRowClick={(row) => handleShowDetails(row as Camera)}
          refreshTrigger={refreshTrigger}
          transformData={transformData}
          useEncryptedNavigation={true}
          encryptedPageName="cameras"
          onAddClick={handleNew}
          onEditClick={handleEdit}
          extraParams={{ 
            tenantId: String(finalTenantId),
            ...(recorderId ? { recorderId: String(recorderId) } : {})
          }}
          entityType="other"
        />
      )}

      {/* Diálogo de Detalhes da Câmera (ORIGINAL) */}
      <Dialog
        open={openDetailsDialog}
        onClose={() => setOpenDetailsDialog(false)}
        maxWidth="md"
        fullWidth
        fullScreen={fullScreen}
      >
        {selectedCamera && (
          <>
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: { xs: 2, sm: 3 },
              }}
            >
              <Typography variant="h6" sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}>
                {selectedCamera.cameraName}
              </Typography>
              <IconButton edge="end" color="inherit" onClick={() => setOpenDetailsDialog(false)} aria-label="close">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}>
                    {t("cameras.basicInfo")}
                  </Typography>
                  <Box sx={{ mt: 1, mb: 2 }}>
                    <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                      <strong>{t("cameras.idLabel")}:</strong> {selectedCamera.cameraId}
                    </Typography>
                    <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                      <strong>{t("cameras.nameField")}:</strong> {selectedCamera.cameraName}
                    </Typography>
                    <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                      <strong>{t("cameras.channelField")}:</strong> {selectedCamera.cameraChannel}
                    </Typography>
                    <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                      <strong>{t("cameras.modelField")}:</strong> {selectedCamera.cameraModel || t("cameras.notSpecified")}
                    </Typography>
                    <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                      <strong>{t("cameras.statusField")}:</strong>
                      <Chip
                        label={selectedCamera.cameraStatus === 1 ? t("common.active") : t("common.inactive")}
                        color={selectedCamera.cameraStatus === 1 ? "success" : "error"}
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    </Typography>
                    {/* As TRÊS regiões. Antes só a baia aparecia aqui, embora
                        bebedouro e comedouro existam desde 15/10 e sejam
                        editáveis no formulário: quem abria os detalhes não
                        tinha como saber se já estavam marcados. */}
                    {([
                      ["cameraRoi", t("stream.layer_roi")],
                      ["cameraRoiWater", t("stream.layer_water")],
                      ["cameraRoiFeed", t("stream.layer_feed")],
                    ] as const).map(([campo, rotulo]) => (
                      <Typography
                        key={campo}
                        sx={{ fontSize: { xs: "0.85rem", sm: "1rem" }, display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}
                      >
                        <strong>{rotulo}:</strong>
                        {(() => {
                          const roi = (selectedCamera as any)[campo]
                          if (!roi) return <Chip label={t("cameras.roiNotDefined")} size="small" variant="outlined" color="default" />
                          try {
                            const pts = typeof roi === "string" ? JSON.parse(roi) : roi
                            return Array.isArray(pts) && pts.length >= 3
                              ? <Chip label={t("cameras.roiPoints", { count: pts.length })} size="small" variant="outlined" color="success" />
                              : <Chip label={t("cameras.roiInvalid")} size="small" variant="outlined" color="warning" />
                          } catch {
                            return <Chip label={t("cameras.roiInvalid")} size="small" variant="outlined" color="warning" />
                          }
                        })()}
                      </Typography>
                    ))}
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}>
                    {t("cameras.location")}
                  </Typography>
                  <Box sx={{ mt: 1, mb: 2 }}>
                    <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                      <strong>{t("cameras.stallField")}:</strong> {selectedCamera.stallName || `-`}
                    </Typography>
                    <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                      <strong>{t("cameras.stallIdField")}:</strong> {selectedCamera.cameraStall || "-"}
                    </Typography>
                    <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                      <strong>{t("cameras.recorderField")}:</strong> {selectedCamera.recorderName || `-`}
                    </Typography>
                    <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                      <strong>{t("cameras.recorderIdField")}:</strong> {selectedCamera.cameraRecorder || "-"}
                    </Typography>
                    <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                      <strong>{t("cameras.horseField")}:</strong> {horsesMap[selectedCamera.cameraStall || 0] || "-"}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      mt: 2,
                      display: "flex",
                      justifyContent: "flex-end",
                      flexDirection: isMobile ? "column" : "row",
                      gap: isMobile ? 1 : 0,
                    }}
                  >
                    {canEditRoi && (
                      <Button
                        variant="outlined"
                        startIcon={<CropFreeIcon />}
                        onClick={() => {
                          setRoiCamera(selectedCamera)
                          setRoiDialogOpen(true)
                        }}
                        disabled={!selectedCamera.cameraRecorder || !selectedCamera.cameraChannel || roiSaving}
                        sx={{ mr: isMobile ? 0 : 2, mb: isMobile ? 1 : 0, width: isMobile ? "100%" : "auto" }}
                      >
                        {parseRoiPoints(selectedCamera.cameraRoi).length >= 3
                          ? t("cameras.roiEdit")
                          : t("cameras.roiDefine")}
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<PlayArrowIcon />}
                      onClick={() => {
                        setOpenDetailsDialog(false)
                        handleShowStream(selectedCamera.cameraId, selectedCamera)
                      }}
                      sx={{ mr: isMobile ? 0 : 2, mb: isMobile ? 1 : 0, width: isMobile ? "100%" : "auto" }}
                      disabled={selectedCamera.cameraStatus !== 1}
                    >
                      {t("cameras.viewStream")}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
          </>
        )}
      </Dialog>

      {/* Editor de ROI (aberto pelo manager sem tenant a partir dos detalhes) */}
      {roiDialogOpen && roiCamera && (
        <RoiEditorDialog
          open={roiDialogOpen}
          onClose={() => {
            setRoiDialogOpen(false)
            setRoiCamera(null)
          }}
          onSave={(regioes) => void handleSaveRoi(regioes)}
          tenantId={Number(finalTenantId)}
          recorderId={Number(roiCamera.cameraRecorder)}
          channel={Number(roiCamera.cameraChannel)}
          cameraName={roiCamera.cameraName || undefined}
          initialPoints={parseRoiPoints(roiCamera.cameraRoi)}
          initialWater={parseRoiPoints((roiCamera as any).cameraRoiWater)}
          initialFeed={parseRoiPoints((roiCamera as any).cameraRoiFeed)}
        />
      )}

      {/* Diálogo de Stream da Câmera */}
      <StreamDialog
  open={openStreamDialog}
  onClose={handleCloseStreamDialog}
  camera={selectedCamera}
  loading={loadingStream}
  tenant_id={finalTenantId}
  streamUrl={streamUrl}
  errorMessage={streamErrorMessage}
  onClearError={() => setStreamErrorMessage(null)}
  onStreamReady={(url: string) => setStreamUrl(url)}
/>

      {/* Snackbar para mensagens */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  )
}

export default CameraList